import { useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Pin, PinOff, Check } from "lucide-react";

export function DefaultViewControl({ screen, current, onApply, className = "" }) {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["default-views"],
    queryFn: async () => (await api.get("/auth/me/default-views")).data,
    staleTime: 60000,
  });
  const appliedRef = useRef(false);
  const onApplyRef = useRef(onApply);
  onApplyRef.current = onApply;
  useEffect(() => {
    if (!data || appliedRef.current) return;
    appliedRef.current = true;
    if (data[screen] && Object.keys(data[screen]).length) onApplyRef.current(data[screen]);
  }, [data, screen]);

  const save = useMutation({
    mutationFn: async () => (await api.put(`/auth/me/default-views/${screen}`, { filters: current })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["default-views"] }); toast.success("Default view saved — this screen now opens like this for you"); },
    onError: () => toast.error("Could not save default view"),
  });
  const clear = useMutation({
    mutationFn: async () => (await api.delete(`/auth/me/default-views/${screen}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["default-views"] }); toast.success("Default view cleared — back to the standard view"); },
    onError: () => toast.error("Could not clear default view"),
  });

  const saved = data?.[screen];
  const has = !!saved && Object.keys(saved).length > 0;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" title="My default view"
          className={`h-9 rounded-sm text-xs shrink-0 ${has ? "border-cyan-300 text-cyan-700 bg-cyan-50/60" : "text-slate-500"} ${className}`}
          data-testid={`default-view-btn-${screen}`}>
          <Pin size={13} className={`mr-1.5 ${has ? "fill-cyan-200" : ""}`} />
          {has ? "Default view" : "Set default"}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="rounded-sm w-64">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-400">My default view — just for your login</DropdownMenuLabel>
        <DropdownMenuItem onClick={() => save.mutate()} data-testid={`default-view-save-${screen}`} className="text-xs cursor-pointer">
          <Check size={13} className="mr-2 text-cyan-600" />
          {has ? "Update default to the current filters" : "Make the current filters my default"}
        </DropdownMenuItem>
        {has && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => clear.mutate()} data-testid={`default-view-clear-${screen}`} className="text-xs text-slate-500 cursor-pointer">
              <PinOff size={13} className="mr-2" /> Clear my default (standard view)
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
